import dayjs from 'dayjs';
import { Form, Spin } from 'antd';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import MainCard from 'components/MainCard';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { useControlData } from '../create/LeaseV9NewTrailModeCreate';
import LeaseV9NewTrailModeScheduleView from './LeaseV9NewTrailModeScheduleView';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import LeaseV9NewTrailModeDetailsForm from '../create/LeaseV9NewTrailModeDetailsForm';
import { utcConverter } from '../../lease-record-management-v9-new-version/utils/helperFunc';
import LeasePageHeader from 'components/header/LeasePageHeader';
import TabPanel from 'components/TabPanel/TabPanel';
import LeaseV9NewTrailModeHistory from './LeaseV9NewTrailModeHistory';
import LeaseV9NewGeneralLedgerView from './LeaseV9NewGeneralLedgerView';
import LeaseV9NewTrailModeBalanceView from './LeaseV9NewTrailModeBalanceView';
import SectionCard from 'components/section/SectionCard';

dayjs.extend(utc);

const LeaseV9NewTrailModeView = ({ screen = 'CREATE' }) => {
	const [form] = Form.useForm();
	const { state } = useLocation();

	const TrialBalanceRef = useRef();
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();

	const [rowIndex, setRowIndex] = useState(0);
	const [selectedTab, setSelectedTab] = useState(0);
	const [selectHistory, setSelectHistory] = useState(null);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);

	const { profileData } = useSelector((state) => state?.profile);

	const { lynFormProperties } = useControlData();
	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const statusData = useMemo(() => {
		return !(
			singleLeaseData?.activationStatus === 'W-I-P' &&
			singleLeaseData?.recordStatus === 'parked' &&
			profileData?.userId === singleLeaseData?.approverUserId[0]
		)
			? singleLeaseData?.data
			: singleLeaseData?.parkedData;
	}, [singleLeaseData]);

	useEffect(() => {
		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			leaseId: '',
			leaseTitle: '',
			leaseType: 'Finance Lease',
			lessorName: '',
			leaseTerm: '',
			leaseEndDate: '',
			creationDate: dayjs(),
			lastModifiedDate: dayjs(),
			escalationPercentage: '0',
			inputChangeMonth: '1',
			grossRentList: [
				{
					from: 1,
					to: '',
					amount: '',
					remarks: '',
				},
			],
			grossRentListSet: [[]],
			advanceAmountListSet: [[]],

			advanceAmountList: [
				{
					from: 1,
					to: '',
					amount: '',
					totalAmount: '',
				},
			],
			advanceAmount: '0',
			advanceDuration: '0',
			upfrontLeasePayment: '',
			initialDirectCost: '',
			securityDeposit: '',
			initialDiscountRate: '0',
			// rightOfUseAssetValue: '10000250 - RoU Front Offices',
			// glleaseLiability: '20001120 - Lease Liabilities',
			// costCenterInterestExpense: '902001 - Finance Common',
			// glInterestExpense: '40004250 - Interest for Lease',
			// leasePayableControlAccount: '20001100 - Lease Payable',
			// glAdvanceRent: '10002050 - Advance Rent',
			// depreciationRoUCostCenter: '902003 - Finance Common',
			// depreciationRoU: '20007555 - Depreciation RoU',
			// accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
			// glBankAccount: '10003001 - SCB Main',
			// leaseExpense: '40004260 - Interest for Lease',
			// leaseExpenseCostCenter: '902002 - Finance Common',
			// securityDepositHead: '10009008 - Security Deposite',
			// accountsPayableHead: '20003300 - Accounts Payable',
			identityId: '',
			cashIncentiveAmount: '',

			paymentModality: 'Start',
			paymentFrequency: 'Monthly',
			endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
			changes: [
				{
					inputChangeEffectiveDate: '',
					inputChangeAdvance: '',
					inputChangeAdvanceAmounts: [],
					inputChangeDescription: 'Initial Rent',
					inputChangeDiscountRate: 0,
					inputChangeEscalation: 0,
					inputChangeGrossAmount: 0,
					inputChangeGrossRents: [],
					inputChangeLeaseTerm: '',
					paymentFrequency: 'Monthly',
					paymentTime: 'Start',
				},
			],
			agreementFiles: [
				{
					id: '',
					name: '',
					file: '',
				},
			],
		}),
		[]
	);

	useEffect(() => {
		if (statusData) {
			const viewValues = {
				// General Lease Information section
				tabIndex: '1',
				modificationSequence: statusData?.modificationSequence,
				leaseId: statusData?.leaseId,
				leaseType: statusData?.leaseType,
				leaseTitle: statusData?.leaseTitle,
				lessorName: statusData?.lessorName,
				lessorID: statusData?.lessorID,
				paymentFrequency: statusData?.paymentFrequency,
				paymentModality: statusData?.paymentModality,
				creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
				lastModifiedDate: dayjs(statusData?.lastModifiedDate),

				// Financial Terms & Conditions section
				advanceAmount: statusData?.advanceAmount,
				advanceDuration: statusData?.advanceDuration,
				grossRent: statusData?.grossRent,
				leaseTerm: statusData?.leaseTerm,
				leaseStartDate: dayjs(statusData?.leaseStartDate),
				leaseEndDate: dayjs(statusData?.leaseEndDate),
				escalationPercentage: statusData?.escalationPercentage,
				initialDiscountRate: statusData?.initialDiscountRate,
				upfrontLeasePayment: statusData?.upfrontLeasePayment ?? 0,
				initialDirectCost: statusData?.initialDirectCost ?? 0,
				securityDeposit: statusData?.securityDeposit ?? 0,
				endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
				// Accounting Information section
				rightOfUseAssetValue: statusData?.rightOfUseAssetValue,
				glleaseLiability: statusData?.glleaseLiability ?? '',
				costCenterInterestExpense: statusData?.costCenterInterestExpense,
				glInterestExpense: statusData?.glInterestExpense,
				leaseExpenseCostCenter: statusData?.leaseExpenseCostCenter,
				leaseExpense: statusData?.leaseExpense,
				leasePayableControlAccount: statusData?.leasePayableControlAccount,
				glAdvanceRent: statusData?.glAdvanceRent,
				depreciationRoUCostCenter: statusData?.depreciationRoUCostCenter,
				depreciationROU: statusData?.depreciationRoU,
				accumDepreciationROU: statusData?.accumDepreciationRoU,
				glBankAccount: statusData?.glBankAccount,
				securityDepositHead: statusData?.securityDepositHead,
				accountsPayableHead: statusData?.accountsPayableHead,
				effectiveDateHistory: statusData?.effectiveDateHistory,
				cashIncentiveAmount: statusData?.cashIncentiveAmount || statusData?.cashIncentive,
				//others
				version: String(statusData?.version),
				leaseLiability: statusData?.leaseLiability,
				amortizationSchedule: statusData?.amortizationSchedule,
				leaseCategory: statusData?.leaseCategory,
				leaseName: statusData?.leaseName,
				contactDetails: statusData?.contactDetails,
				identityId: singleLeaseData?._id,

				renewalOptions: statusData?.renewalOptions,
				terminationOption: statusData?.terminationOption,
				month: statusData?.month,
				refundability: statusData?.refundability,
				agreementFiles: statusData?.agreementFiles,

				responsibility: statusData?.responsibility,
				basisOfRate: statusData?.basisOfRate,
				nonLeaseComponents: statusData?.nonLeaseComponents,
				separateServiceCharges: statusData?.separateServiceCharges,
				modificationDetails: statusData?.modificationDetails,
				discountRateChanges: statusData?.discountRateChanges,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				grossRentList: statusData?.grossRentList,
				grossRentListSet: statusData?.grossRentListSet,
				advanceAmountListSet: statusData?.advanceAmountListSet,
				advanceAmountList: statusData?.advanceAmountList,
				//glValues
				gl_rouAssetInitialRecognition: statusData?.gl_rouAssetInitialRecognitionValue,
				gl_rouAssetModification: statusData?.gl_rouAssetModificationValue,
				gl_rouAssetPrepayments: statusData?.gl_rouAssetPrepaymentsValue,
				gl_rouAssetUpfrontPayment: statusData?.gl_rouAssetUpfrontPaymentValue,

				gl_rouAssetIndirectCosts: statusData?.gl_rouAssetIndirectCostsValue,
				gl_rouCashIncentive: statusData?.gl_rouCashIncentiveValue,
				gl_rouAssetKnockOff: statusData?.gl_rouAssetKnockOffValue,
				gl_securityDeposit: statusData?.gl_securityDepositValue,
				gl_leaseLiabilityInitialRecognition: statusData?.gl_leaseLiabilityInitialRecognitionValue,
				gl_leaseLiabilityModification: statusData?.gl_leaseLiabilityModificationValue,
				gl_leaseLiabilityPayment: statusData?.gl_leaseLiabilityPaymentValue,
				gl_leaseLiabilityInterest: statusData?.gl_leaseLiabilityInterestValue,
				gl_interestExpense: statusData?.gl_interestExpenseValue,
				gl_depreciationRou: statusData?.gl_depreciationRouValue,
				gl_accumDepreciationRouDepreciation: statusData?.gl_accumDepreciationRouDepreciationValue,
				gl_accumDepreciationRouKnockOff: statusData?.gl_accumDepreciationRouKnockOffValue,
				gl_apControlLedgerPrepayments: statusData?.gl_apControlLedgerPrepaymentsValue,
				gl_apControlLedgerUpfrontPayment: statusData?.gl_apControlLedgerUpfrontPaymentValue,
				gl_apControlLedgerIndirectCosts: statusData?.gl_apControlLedgerIndirectCostsValue,
				gl_apControlLedgerSecurityDeposit: statusData?.gl_apControlLedgerSecurityDepositValue,
				gl_apControlLedgerPayment: statusData?.gl_apControlLedgerPaymentValue,
				gl_arCashIncentive: statusData?.gl_arCashIncentiveValue,

				//changes
				changes: statusData?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
					};
				}),
			};

			setRowIndex(statusData?.changes?.length);

			form.setFieldsValue(viewValues);

			requestAnimationFrame(() => {
				setDataFullyLoaded(true);
			});
		}
	}, [singleLeaseData]);

	const selectTabHandler = (_, newValue) => {
		setSelectedTab(newValue);

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 3 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (TrialBalanceRef.current?.loadData) {
				TrialBalanceRef.current.loadData();
			}
		}
	};

	useEffect(() => {
		if (singleLeaseData && singleLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = singleLeaseData.activationStatus;
		}
	}, [singleLeaseData]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: <LeaseV9NewTrailModeDetailsForm viewData={singleLeaseData} screen="VIEW" vendorList={lynFormProperties?.vendorList} />,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<LeaseV9NewTrailModeScheduleView
						// screen={screen}
						// ref={leaseScheduleRef}
						viewData={singleLeaseData}
						dataFullyLoaded={dataFullyLoaded}
					/>
				</>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: (
				<>
					<LeaseV9NewTrailModeHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</>
			),
		},
		{
			key: '4',
			label: 'General Ledgers',
			children: <LeaseV9NewGeneralLedgerView />,
		},
		{
			key: '5',
			label: 'Trial Balance',
			children: (
				// <>
				// 	<CardAccordion
				// 		title=""
				// 		customHeaderSX={{
				// 			backgroundColor: '#e1f0ff !important',
				// 			p: '10px',
				// 		}}
				// 		typographySX={{ fontSize: '16px !important' }}
				// 		darkTitle={true}
				// 		sx={{ mb: 2 }}
				// 	>
				// 		<TrialBalance ref={TrialBalanceRef} />
				// 	</CardAccordion>
				// </>

				<LeaseV9NewTrailModeBalanceView />
			),
		},
	];

	return (
		<Box>
			<Spin spinning={isViewLoading} style={{ maxHeight: '100%' }}>
				<LeasePageHeader
					createBtn={false}
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					title={`View ${state?.processName}`}
					secondaryTitle={`Lease ID: ${statusData?.leaseId || ''}`}
				/>

				<SectionCard>
					<Form form={form} layout="vertical" autoComplete="off" requiredMark={false} initialValues={initialFormValues}>
						<AntdValuePreview form={form} show={false} />

						{tabItem?.map((item, index) => (
							<Box
								key={index}
								style={{
									display: selectedTab === index ? 'block' : 'none',
								}}
							>
								{item.children}
							</Box>

							// <TabPanel key={index} value={selectedTab} index={index}>
							// 	{item.children}
							// </TabPanel>
						))}
					</Form>
				</SectionCard>

				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						selectItem={selectHistory}
						setSelectHistory={setSelectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default LeaseV9NewTrailModeView;
